import React, { useState, useEffect } from 'react';
import update from 'immutability-helper';

import API from 'files/api.js';
import Abstract from 'classes/Abstract.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import { ClientSelector } from 'managers/Clients.js';
import { PaymentsOverview, UserAvailabilityEditor } from 'managers/Users.js';
import Request from 'files/Request.js';
import { Spring } from '@react-spring/core';
import Spline from '@splinetool/react-spline';
import Task from 'classes/Task.js';
import User from 'classes/User.js';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

const Sidebar = ({ active, activeClient, content, onLogoutClick, onMobileClose, onClick, user, utils }) => {

    const [leadCount, setLeadCount] = useState(activeClient?.lead_count || 0);
    const [loading, setLoading] = useState(false);
    const [openMenus, setOpenMenus] = useState([]);
    const [springToggle, setSpringToggle] = useState(null);
    const [tasks, setTasks] = useState([]);

    const onAvailabilityClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                type: 'users',
                object: user
            }),
            id: `user_availability_${user.user_id}`,
            Component: UserAvailabilityEditor
        });
    }

    const onCancelTask = task => {
        utils.alert.show({
            title: 'Cancel Task',
            message: 'Are you sure that you want to cancel this task? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Do Not Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onCancelTaskConfirm(task);
                    return;
                }
            }
        });
    }

    const onCancelTaskConfirm = async task => {
        try {
            setLoading(task.id);
            await Utils.sleep(0.25);
            await Request.post(utils, '/utils/', {
                type: 'cancel_task',
                id: task.id
            });

            setLoading(false);
            setTasks(tasks => {
                return tasks.filter(prev_task => prev_task.id !== task.id);
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue cancellin this task. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onRemoveTask = index => {
        setTasks(tasks => {
            return update(tasks, {
                $splice: [[ index, 1 ]]
            });
        });
    }

    const onSelectClient = () => {
        utils.layer.open({
            id: 'client_selector',
            Component: ClientSelector
        });
    }

    const onToggleMenu = key => {
        if(openMenus.includes(key)) {
            setOpenMenus(openMenus => update(openMenus, {
                $splice: [
                    [openMenus.indexOf(key), 1]
                ]
            }));
            return;
        }
        setOpenMenus(openMenus => update(openMenus, {
            $push: [key]
        }));
    }

    const onUserComponentClick = evt => {
        utils.sheet.show({
            sort: false,
            items: [{
                key: 'payments',
                title: 'Credits and Payments',
                style: 'default',
                visible: false
            },{
                key: 'profile',
                title: 'My Profile',
                style: 'default',
                visible: false
            },{
                key: 'logout',
                title: 'Logout',
                style: 'default'
            }],
            position: 'top',
            target: evt.target
        }, key => {
            switch(key) {
                case 'logout':
                onLogoutClick();
                break;

                case 'payments':
                utils.layer.open({
                    id: `payments_overview_${user.user_id}`,
                    abstract: Abstract.create({
                        type: 'users',
                        object: user
                    }),
                    Component: PaymentsOverview
                })
                break;
            }
        });
    }

    const onUserTaskProgressChange = data => {
        try {
            setTasks(tasks => {
                if(!tasks.find(task => task.id === data.id)) {
                    return update(tasks, {
                        $push: [data]
                    });
                }
                return tasks.map(task => {
                    return task.id === data.id ? data : task;
                });
            });
        } catch(e) {
            console.error(e.message);
        }
    }

    const isItemRestricted = item => {
        return utils.user.get().level > item.level;
    }

    const getContent = () => {

        if(!user) {
            return null;
        }

        let backgroundColor = Appearance.colors.background();
        return (
            <div
            className={'mt-0'}
            style={{
                alignItems: 'center',
                backgroundColor: backgroundColor,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'center',
                padding: 16,
                position: 'relative',
                zIndex: 1100
            }}>
                <div style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 24,
                    width: '100%'
                }}>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        height: 150,
                        transform: 'scale(0.75)',
                        width: '100%'
                    }}>
                        <div style={{
                            height: 175,
                            position: 'relative',
                            overflow: 'hidden',
                            width: 250
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: -175,
                            }}>
                                <Spline 
                                scene={window.theme === 'dark' ? 'https://prod.spline.design/05Rlu1Ed0BeAjoHQ/scene.splinecode' : 'https://prod.spline.design/05Rlu1Ed0BeAjoHQ/scene.splinecode'} />
                            </div>
                        </div>
                    </div>
                    <span style={{
                        color: Appearance.colors.text(),
                        fontSize: 18,
                        fontWeight: 800
                    }}>{getTitle()}</span>
                    <span style={{
                        color: Appearance.colors.subText(),
                        fontSize: 14,
                        fontWeight: 600
                    }}>{getSubTitle()}</span>
                    {getTaskProgressComponents()}
                    {getSelector()}
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    flex: 1,
                    height: '100%',
                    minHeight: 0,
                    minWidth: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative',
                    width: '100%'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        flex: 1,
                        height: '100%',
                        minHeight: 0,
                        minWidth: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingBottom: 125,
                        position: 'relative',
                        width: '100%'
                    }}>
                        {getContentComponents()}
                    </div>
                    <div style={{
                        background: `linear-gradient(0deg, ${backgroundColor}, ${backgroundColor}, ${Utils.hexToRGBA(backgroundColor, 0)})`,
                        bottom: 0,
                        height: 125,
                        position: 'absolute',
                        width: '100%',
                    }} />
                </div>
                <div style={{
                    display: 'flex',
                    flex: 'none',
                    flexDirection: 'column',
                    textAlign: 'center',
                    width: '100%',
                }}>
                    {getUserComponent()}
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>{`Version ${API.app_version} Build ${API.build}`}</span>
                </div>
            </div>
        )
    }

    const getContentComponents = () => {

        // loop through targets and render components
        return content.map(item => {

            // determine if the item has subviews
            if(item.subViews) {

                // render a dropdown if at least one visible item if available
                let items = Object.values(item.subViews);
                if(items.length >= 1) {
                    return getItem({
                        ...item,
                        active: active && item.key === active.view,
                        items: items.map(subView => {
                            return {
                                ...subView,
                                active: active && item.key === active.view && active.subView === subView.key,
                                onClick: onClick
                            }
                        })
                    })
                }

                // render a singular item as a fallback if at least one panel was provided
                return items[0] && items[0].panels.length > 0 && getItem({
                    ...item,
                    active: active && active.view === item.key,
                    key: items[0].key,
                    onClick: onClick.bind(this, {
                        action: item.action,
                        subView: items[0].key,
                        view: item.key
                    })
                })
            }

            return getItem({
                ...item,
                active: active && active.view === item.key,
                onClick: onClick.bind(this, {
                    action: item.action,
                    view: item.key
                })
            })
        });
    }

    const getItem = ({ active, icon, items, key, onClick, title }) => {

        let targets = items?.filter(item => isItemRestricted(item) !== true) || [];
        let shouldShowNavigationItem = items ? targets.length > 0 : true;

        return shouldShowNavigationItem === true && (
            <div
            key={key}
            className={'cursor-pointer'}
            onClick={items ? onToggleMenu.bind(this, key) : onClick}
            onMouseEnter={setSpringToggle.bind(this, key)}
            onMouseLeave={setSpringToggle.bind(this, null)}
            style={{
                paddingLeft: 12,
                paddingRight: Utils.isMobile() ? 12 : 0,
            }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: active && 'rgba(255,255,255,0.5)',
                    borderRadius: active ? 15 : null,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: '8px 12px 8px 8px',
                    width: '100%'
                }}>
                    <Spring
                    config={{ mass: 1, tension: 180, friction: 8 }}
                    from={{ transform: 'scale(1)' }}
                    to={{ transform: springToggle === key ? 'scale(1.15)' : 'scale(1.0)' }}
                    toggle={springToggle === key ? true : false}>
                    {props => (
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flex: 'none',
                            height: 40,
                            justifyContent: 'center',
                            marginRight: 12,
                            overflow: 'visible',
                            width: 40,
                            ...props
                        }}>
                            <img
                            src={`images/${icon}${window.theme === 'dark' ? '-dark' : '-light'}${active ? '-selected' : ''}.png`}
                            style={{
                                height: '100%',
                                objectFit: 'contain',
                                width: '100%'
                            }} />
                        </div>
                    )}
                    </Spring>

                    <span style={{
                        color: active ? Appearance.colors.primary() : Appearance.colors.text(),
                        flexGrow: 1,
                        fontSize: 14,
                        fontWeight: 500,
                        overflow: 'hidden',
                        paddingRight: 12,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                    }}>{title}</span>
                    <div style={{
                        backgroundColor: active ? Appearance.colors.primary() : Appearance.colors.transparent,
                        borderRadius: 1.5,
                        height: 25,
                        overflow: 'hidden',
                        width: 3
                    }} />
                </div>
                {items && openMenus.includes(key) && (
                    <div style={{
                        backgroundColor: Appearance.colors.layerBackground(),
                        borderRadius: 10,
                        boxShadow: '-10px 10px 15px rgba(0,0,0,0.025)',
                        marginBottom: 24,
                        marginLeft: 30,
                        marginTop: 8,
                        overflow: 'hidden'
                    }}>
                        {targets.map((item, index, items) => {
                            return (
                                <div
                                key={item.key}
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%'
                                }}>
                                    <a
                                    className={`dropdown-item ${window.theme}`}
                                    onClick={() => {
                                        setOpenMenus([]);
                                        item.onClick({
                                            action: item.action,
                                            level: item.level,
                                            subView: item.key,
                                            view: key
                                        });
                                    }}
                                    style={{
                                        borderBottom: index !== items.length - 1 ? `1px solid ${Appearance.colors.divider()}` : null,
                                        fontSize: 13,
                                        ...getLabelStyles(item)
                                    }}>{item.title}</a>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        )
    }

    const getLabelStyles = item => {
        if(isItemRestricted(item)) {
            return {
                color: Appearance.colors.red,
                fontWeight: 600
            };
        }
        return {
            color: item.active ? Appearance.colors.primary() : Appearance.colors.text(),
            fontWeight: 400
        }
    }

    const getSelector = () => {
        let canChangeClient = user.level < User.levels.get().primary;
        return canChangeClient && (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                marginTop: 24,
                width: '100%'
            }}>
                <Button
                color={'primary'}
                label={'View Other Clients'}
                onClick={onSelectClient}
                type={'large'} />
            </div>
        )
    }

    const getSubTitle = () => {
        if(activeClient) {
            return activeClient.address ? activeClient.address.street_address_1 : 'Address Not Available';
        }
    }

    const getTaskModifierButton = (task, index) => {
        if(task.complete) {
            return (
                <img
                className={'text-button'}
                src={'images/close-button-light-small.png'}
                onClick={onRemoveTask.bind(this, index)}
                style={{
                    height: 20,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 20
                }}/>
            )
        }
        return (
            <img
            className={'text-button'}
            src={'images/red-x-icon.png'}
            onClick={onCancelTask.bind(this, task)}
            style={{
                height: 20,
                marginLeft: 8,
                objectFit: 'contain',
                width: 20
            }}/>
        )
    }

    const getTaskProgressValue = task => {
        switch(task.category) {
            case Task.categories.lead_import:
            if(task.complete) {
                return `${Utils.softNumberFormat(task.total)} ${task.total === 1 ? 'lead' : 'leads'} have been imported`;
            }
            return `${Utils.softNumberFormat(task.progress)} ${task.progress === 1 ? 'lead' : 'leads'} imported...`;

            case Task.categories.lead_address_conversion:
            if(task.complete) {
                return `${Utils.softNumberFormat(task.total)} ${task.total === 1 ? 'lead' : 'leads'} have been geocoded`;
            }
            return `${Utils.softNumberFormat(task.progress)} ${task.progress === 1 ? 'lead' : 'leads'} geocoded...`;

            default:
            return 'Unknown progress value';
        }
    }

    const getTaskProgressComponents = () => {
        if(tasks.length === 0) {
            return null;
        }
        return (
            <div style={{
                marginTop: 20,
                paddingLeft: 15,
                paddingRight: 15,
                width: '100%'
            }}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    border: `1px solid ${Appearance.colors.softBorder()}`,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    {tasks.map((task, index) => {
                        return (
                            Views.entry({
                                bottomBorder: index !== tasks.length - 1,
                                icon: {
                                    className: task.complete ? null : 'rotate',
                                    path: task.complete ? 'images/checkmark-button-green-small.png' : 'images/task-progress-light-grey.png'
                                },
                                key: index,
                                loading: loading === task.id,
                                rightContent: getTaskModifierButton(task, index),
                                subTitle: getTaskProgressValue(task),
                                title: getTaskTitle(task)
                            })
                        )
                    })}
                </div>
            </div>
        )
    }

    const getTaskTitle = task => {
        switch(task.category) {
            case Task.categories.lead_import:
            return task.complete ? 'Lead Import Complete' : 'Lead Import In Progress';

            case Task.categories.lead_address_conversion:
            return task.complete ? 'Lead Geocoding Complete' : 'Lead Geocoding In Progress';

            default:
            return 'Unknown Task';
        }
    }

    const getTitle = () => {
        if(activeClient) {
            return activeClient.name;
        }
    }

    const getUserComponent = () => {
        if(!user) {
            return null;
        }
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        border: `2px solid ${Appearance.colors.softBorder()}`,
                        width: '100%'
                    }}>
                        {Views.entry({
                            bottomBorder: false,
                            icon: {
                                path: user.avatar
                            },
                            rightContent: (
                                <div style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    marginLeft: 12,
                                    minWidth: 0
                                }}>
                                    <img 
                                    alt={'Availability'}
                                    className={'text-button'}
                                    onClick={onAvailabilityClick}
                                    src={window.theme === 'dark' ? 'images/availability-dark.png' : 'images/availability-light.png'}
                                    style={{
                                        height: 30,
                                        width: 30
                                    }} 
                                    title={'Availability'} />
                                    <img 
                                    alt={'Logout'}
                                    className={'text-button'}
                                    onClick={onLogoutClick}
                                    src={window.theme === 'dark' ? 'images/logout-dark.png' : 'images/logout-light.png'}
                                    style={{
                                        height: 30,
                                        marginLeft: 4,
                                        width: 30
                                    }} 
                                    title={'Logout'} />
                                </div>
                            ),
                            subTitle: User.levels.toText(user.level),
                            title: user.full_name
                        })}
                    </div>
                </div>
                <div style={{
                    backgroundColor: Appearance.colors.softBorder(),
                    height: 1,
                    marginBottom: 16,
                    marginTop: 16,
                    width: 50
                }} />
            </div>
        )
    }

    const connectToSockets = async () => {
        try {
            if(user) {
                await utils.sockets.persistOn('tasks', `on_user_task_progress_change_${user.user_id}`, onUserTaskProgressChange);
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    const disconnectFromSockets = async () => {
        try {
            if(user) {
                await utils.sockets.off('tasks', `on_user_task_progress_change_${user.user_id}`, onUserTaskProgressChange);
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        setLeadCount(activeClient?.lead_count || 0);
    }, [activeClient]);

    useEffect(() => {
        connectToSockets();
        return disconnectFromSockets;
    }, [user]);

    return getContent();
}

export default Sidebar;
